import type * as ld from "launchdarkly-js-client-sdk";
import { LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";
import type { Flag } from "@medbillai/utils/flag";
import { type FlagBootstrap, FlagBootstrapProvider, useFlagBootstrap, useRecordExposure } from "./bootstrap";
import { getRuntimeFlagOverride, useRuntimeFlagOverride } from "./runtime-overrides";
import { FlagContext, type FlagProviderProps, isAnonymousLdContext, type LDIdentifyFunction, ldContextIdentityKey, ldLogger, retryIdentify, useFlagContext, useFlagContextSetup, useFlagHasIdentified, useFlagIdentifyGeneration } from "./shared";
const clientSideId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID;

// Module-level client reference for non-hook access (e.g., from analytics service)
let _webClient: ld.LDClient | undefined;

/** Track a metric event to LaunchDarkly for experimentation. */
export const trackToLaunchDarkly = ({
  metricKey,
  data,
  metricValue
}: {
  metricKey: string;
  data?: Record<string, unknown>;
  metricValue?: number;
}) => {
  if (!_webClient) {
    return;
  }
  _webClient.track(metricKey, data, metricValue);
};

// Component to capture client reference for non-hook access
const LDClientCapture = () => {
  const client = useLDClient();
  useEffect(() => {
    _webClient = client ?? undefined;
  }, [client]);
  return null;
};
export const FlagProvider = ({
  children,
  bootstrap
}: FlagProviderProps & {
  bootstrap?: FlagBootstrap;
}) => {
  const {
    hasIdentified,
    floorReached,
    identifyGeneration,
    startIdentify,
    finishIdentify,
    declareFlagIdentity
  } = useFlagContextSetup();
  const flagContextValue = useMemo(() => ({
    hasIdentified,
    floorReached,
    identifyGeneration,
    startIdentify,
    finishIdentify,
    declareFlagIdentity
  }), [hasIdentified, floorReached, identifyGeneration, startIdentify, finishIdentify, declareFlagIdentity]);
  return <FlagContext.Provider value={flagContextValue} data-sentry-element="FlagContext.Provider" data-sentry-component="FlagProvider" data-sentry-source-file="index.tsx">
      <LDProvider clientSideID={clientSideId ?? ""}
    // Start the browser client on the server's context and values. Without
    // this it mints its own anonymous key, so the server renders the arm
    // for one key while the browser enrols another.
    context={bootstrap?.status === "ready" ? bootstrap.context as ld.LDContext : undefined} options={{
      logger: ldLogger,
      application: {
        id: "medbillai-next"
      },
      // Default is false, which makes `allFlags` emit evaluation events
      // too. The exposure guard reads through `allFlags` precisely to
      // avoid enrolling anyone, so it has to stay silent.
      sendEventsOnlyForVariation: true,
      ...(bootstrap?.status === "ready" ? {
        bootstrap: bootstrap.flags as ld.LDFlagSet
      } : {})
    }} timeout={3} data-sentry-element="LDProvider" data-sentry-source-file="index.tsx">
        <LDClientCapture data-sentry-element="LDClientCapture" data-sentry-source-file="index.tsx" />
        <FlagBootstrapProvider bootstrap={bootstrap} data-sentry-element="FlagBootstrapProvider" data-sentry-source-file="index.tsx">
          {children}
        </FlagBootstrapProvider>
      </LDProvider>
    </FlagContext.Provider>;
};
export const useLaunchDarklyIdentify = () => {
  const client = useLDClient();
  const {
    startIdentify,
    finishIdentify
  } = useFlagContext();
  const identify = useCallback<LDIdentifyFunction>(async (context, options) => {
    if (!client) {
      // This is common on web, where the client is asynchronously
      // initialized, which requires a call to LD. The auth provider includes
      // `identify` in its `useEffect`, so it will automatically retry after
      // the client is available and the identify callback updates.
      return;
    }
    const existingContext = client.getContext();
    const newContext = typeof context === "function" ? context(existingContext) : context;
    // Anonymous placeholder identifies (auth waiting on profile) must not
    // unlock variation(). Intentional logged-out anon can opt in via options.
    const authorizeFlagReads = options?.authorizeFlagReads ?? !isAnonymousLdContext(newContext);
    let succeeded = false;
    try {
      startIdentify({
        authorizeFlagReads,
        identityKey: options?.identityKey
      });
      succeeded = await retryIdentify(async () => {
        await client.identify(newContext);
      });
    } finally {
      // Always pair start/finish even on failure so the in-flight count
      // can't stick open. Reads latch only when the batch asked to
      // authorize, reached LaunchDarkly, and left it on the declared
      // identity — read back rather than assumed, since `updateKind`
      // enrichment builds on whatever context the client already had.
      finishIdentify(succeeded, succeeded ? ldContextIdentityKey(client.getContext()) : undefined);
    }
  },
  // client doesn't actually change reference as the context updates etc, so
  // this will prevent the callback from ever being recreated.
  [client, startIdentify, finishIdentify]);
  return identify;
};

/**
 * Hook for retrieving typed feature flags with Zod schema validation.
 *
 * On a page wrapped by `withLaunchDarklyFlags` the value comes from the
 * server-evaluated snapshot: correct on the first render, immutable for the
 * life of the page, and subject to no readiness gate. Elsewhere this behaves
 * exactly as it did before — the same browser SDK read behind the same latch —
 * so wrapping a page is the only thing that changes its behaviour.
 *
 * @param schema - Zod schema for validation
 * @param flag - The feature flag key
 * @param defaultValue - Default value when flag is unavailable or validation fails
 * @returns The typed flag value
 */
export const useTypedVariation = <T,>(schema: z.ZodType<T>, flag: Flag, defaultValue: T): T => {
  const client = useLDClient();
  const override = useRuntimeFlagOverride(flag);
  const bootstrap = useFlagBootstrap();
  const bootstrapped = bootstrap?.status === "ready";
  // Only consulted on the unwrapped path. FlagProvider sits above AuthProvider,
  // so the client starts on an anonymous context; the `hasIdentified` latch (an
  // identify for the current identity *succeeded* — never the UX floor alone)
  // is what stops a slow LD init evaluating against it. `identifyGeneration`
  // recomputes the memo after each identify batch — `client` keeps the same
  // reference.
  const hasIdentified = useFlagHasIdentified();
  const identifyGeneration = useFlagIdentifyGeneration();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we dont want the developer using this hook to worry about providing a stable reference to the schema to avoid unnecessary re-renders
  const memoizedSchema = useMemo(() => schema, []);
  useRecordExposure(flag);

  // biome-ignore lint/correctness/useExhaustiveDependencies: identifyGeneration is intentionally unused in the body — it exists solely to recompute after each identify batch while `client` stays referentially stable
  return useMemo(() => {
    if (override !== undefined) {
      const parsedOverride = memoizedSchema.safeParse(override);
      if (parsedOverride.success) {
        return parsedOverride.data;
      }
    }

    // On a wrapped page the snapshot is the whole answer, so no readiness gate
    // applies. On an unwrapped one nothing has changed: same client read behind
    // the same latch, which is what keeps adoption per-page.
    const rawValue = bootstrapped ? bootstrap.flags[flag as string] : client && hasIdentified ? client.variation(flag as string, defaultValue) as unknown : undefined;
    if (rawValue === undefined) {
      return defaultValue;
    }
    const parsed = memoizedSchema.safeParse(rawValue);
    if (!parsed.success) {
      console.warn("Type mismatch: expected", JSON.stringify(memoizedSchema), "but got", typeof rawValue, "returning defaultValue:", defaultValue);
      return defaultValue;
    }
    return parsed.data;
  }, [bootstrapped, bootstrap, client, hasIdentified, identifyGeneration, memoizedSchema, flag, defaultValue, override]);
};
export const useBoolVariation = (flag: Flag, defaultValue: boolean) => useTypedVariation(z.boolean(), flag, defaultValue);
export const useStringVariation = (flag: Flag, defaultValue: string) => useTypedVariation(z.string(), flag, defaultValue);
export const useNumberVariation = (flag: Flag, defaultValue: number) => useTypedVariation(z.number(), flag, defaultValue);
export const useEnumVariation = <T extends string,>(allowable: readonly [T, ...T[]], flag: Flag, defaultValue: T): T => useTypedVariation(z.enum(allowable), flag, defaultValue);

/**
 * Non-hook enum-flag evaluation for imperative call sites. Evaluating a flag
 * (not merely holding its value) is what enrolls a user in any experiment
 * attached to it, so call this at the moment the user can actually receive
 * the treatment — never from the render path of an always-mounted component.
 */
export const evaluateEnumVariation = <T extends string,>(allowable: readonly [T, ...T[]], flag: Flag, defaultValue: T): T => {
  const schema = z.enum(allowable);
  const override = schema.safeParse(getRuntimeFlagOverride(flag));
  if (override.success) {
    return override.data;
  }
  if (!_webClient) {
    return defaultValue;
  }
  const parsed = schema.safeParse(_webClient.variation(flag as string, defaultValue) as unknown);
  return parsed.success ? parsed.data : defaultValue;
};
export * from "./bootstrap";